'use client';

import { Flex, Checkbox } from '@/_components';
import {
  useCallback,
  useState,
  useEffect,
  useContext,
  useMemo,
  type FC,
} from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import {
  readAnswerArray,
  toggleAnswerOption,
  getHasRolloverItem,
} from '@/_utils/questions';
import type { QuestionFormCheckboxProps } from './QuestionFormCheckbox.types';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { Question } from '@/_types/models/questionnaire';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { useDebounce } from '@/_hooks/useDebounce';
import { SAVE_TOGGLE_DELAY_MS } from '@/_constants/questions';

export const QuestionFormCheckbox: FC<QuestionFormCheckboxProps> = ({
  question,
  isDisabled,
  onChange,
  mode,
}) => {
  const { updateQuestion, selectedQuestions, selectedQuestionnaire } =
    useContext(ClientQuestionnaireContext);
  const { updatePreviewQuestion, previewQuestions } =
    useContext(PreviewContext);
  const { updateTemplateQuestion, previewTemplate } =
    useContext(UserPreviewContext);

  const [answerToDebounce, setAnswerToDebounce] = useState<
    | {
        questionId: number;
        answer: string;
      }
    | undefined
  >(undefined);

  const debouncedAnswer = useDebounce(answerToDebounce, SAVE_TOGGLE_DELAY_MS);

  const answerQuestion = useCallback(
    async (answerParams: { questionId: number; answer: string }) => {
      if (mode === 'admin-preview') {
        await updatePreviewQuestion({ ...answerParams });
      } else if (mode === 'admin-template') {
        await updateTemplateQuestion(answerParams);
      } else {
        await updateQuestion({ ...answerParams, isAsync: true });
      }
    },
    [mode, updateQuestion, updateTemplateQuestion, updatePreviewQuestion],
  );

  useEffect(() => {
    if (debouncedAnswer) {
      answerQuestion(debouncedAnswer);
    }
  }, [debouncedAnswer]); // eslint-disable-line react-hooks/exhaustive-deps

  const answers: Array<string> = useMemo(
    () => readAnswerArray(question?.answer),
    [question?.answer],
  );

  const questions: Array<Question> = useMemo(() => {
    if (mode === 'admin-preview') return previewQuestions;
    if (mode === 'admin-template') {
      return previewTemplate?.questionnaire_questions || [];
    }

    return selectedQuestions;
  }, [
    mode,
    previewQuestions,
    selectedQuestions,
    previewTemplate?.questionnaire_questions,
  ]);

  return (
    <Flex isColumn={true} gap={4}>
      {question?.options?.map((option, key) => {
        const onAnswer = async (event?: any) => {
          event?.preventDefault?.();

          const newAnswers: string = toggleAnswerOption(answers, option);
          const answerParams = { questionId: question.id, answer: newAnswers };

          onChange?.(newAnswers);
          setAnswerToDebounce(answerParams);
        };

        const previousYear: string = selectedQuestionnaire?.tax_year
          ? `in ${(selectedQuestionnaire?.tax_year ?? 0) - 1}`
          : 'last year';
        const isChecked: boolean = answers.includes(option);

        const hasRolloverItem: boolean = getHasRolloverItem(
          questions,
          question.id,
          option,
        );

        const pillText: string | undefined = hasRolloverItem
          ? mode !== 'client'
            ? `Client had this ${previousYear}`
            : `You had this ${previousYear}`
          : undefined;

        return (
          <Checkbox
            key={key}
            label={option}
            isDisabled={isDisabled}
            pillText={pillText}
            isChecked={isChecked}
            onChange={onAnswer}
            withGlossary={true}
          />
        );
      })}
    </Flex>
  );
};
